import { supabase, isSupabaseConfigured } from "@/lib/supabaseClient";
import { vocabularyData } from "@/data/mockData";
import { streakService } from "./streakService";
import { buildWordDuplicateKey, getValidUuidOrNull, isUniqueConstraintError, toNullableUuid, handleSupabaseError } from "@/utils/duplicateUtils";
import { flashcardService } from "./flashcardService";
import i18n from "@/i18n";

export interface WordBankRecord {
  id: string;
  term: string;     // maps to 'word' on Supabase
  meaning: string;  // maps to 'translation' on Supabase
  context: string;  // maps to 'example_sentence' on Supabase
  category: string;
  tags?: any;
  metadata?: any;
  createdAt?: string;
  updatedAt?: string;
}

const STORAGE_KEY = "tef_custom_vocabulary";

export function mapSupabaseToWord(row: any): WordBankRecord {
  return {
    id: row.id,
    term: row.word || "",
    meaning: row.translation || row.definition || "",
    context: row.example_sentence || "",
    category: row.category || "Substantivo",
    tags: row.tags || [],
    createdAt: row.created_at,
    updatedAt: row.updated_at
  };
}

export function mapWordToSupabase(word: any): any {
  const dupKey = buildWordDuplicateKey(word);

  let sanitizedTags: string[] = [];
  if (typeof word.tags === "string") {
    sanitizedTags = (word.tags as string).split(",").map(t => t.trim()).filter(Boolean);
  } else if (Array.isArray(word.tags)) {
    sanitizedTags = word.tags.map((t: any) => String(t).trim()).filter(Boolean);
  }

  let sanitizedMetadata: any = {};
  if (typeof word.metadata === "string") {
    try {
      sanitizedMetadata = JSON.parse(word.metadata);
    } catch {
      sanitizedMetadata = { note: word.metadata };
    }
  } else if (word.metadata && typeof word.metadata === "object") {
    sanitizedMetadata = { ...word.metadata };
  }

  return {
    word: (word.term || word.word || "").trim(),
    translation: (word.meaning || word.translation || "").trim(),
    definition: (word.definition || word.meaning || word.translation || "").trim(),
    example_sentence: word.context || word.example_sentence || "",
    example_translation: word.exampleTranslation || word.example_translation || "",
    language: word.language || "fr",
    category: word.category || "Vocabulário",
    tags: sanitizedTags,
    source_question_id: toNullableUuid(word.sourceQuestionId ?? word.source_question_id),
    source_test_id: toNullableUuid(word.sourceTestId ?? word.source_test_id),
    metadata: sanitizedMetadata,
    duplicate_key: dupKey
  };
}

export const wordBankService = {
  // Get all vocabulary words
  async getWords(): Promise<WordBankRecord[]> {
    if (!isSupabaseConfigured || !supabase) {
      return this.getLocalWords();
    }
    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData?.user) {
        return [];
      }
      const { data, error } = await supabase
        .from("word_bank")
        .select("*")
        .eq("user_id", userData.user.id)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return (data || []).map(mapSupabaseToWord);
    } catch (err: any) {
      throw handleSupabaseError(err, "word");
    }
  },

  // Get single word
  async getWordById(id: string): Promise<WordBankRecord | null> {
    if (!isSupabaseConfigured || !supabase) {
      const local = this.getLocalWords();
      return local.find(w => w.id === id) || null;
    }
    try {
      if (id.startsWith("vocab_") || !getValidUuidOrNull(id)) {
        const local = this.getLocalWords();
        return local.find(w => w.id === id) || null;
      }
      const { data: userData } = await supabase.auth.getUser();
      if (!userData?.user) {
        return null;
      }
      const { data, error } = await supabase
        .from("word_bank")
        .select("*")
        .eq("id", id)
        .eq("user_id", userData.user.id)
        .single();

      if (error) throw error;
      return data ? mapSupabaseToWord(data) : null;
    } catch (err: any) {
      throw handleSupabaseError(err, "word");
    }
  },

  // Create word
  async createWord(word: Omit<WordBankRecord, "id"> & { id?: string }): Promise<WordBankRecord> {
    streakService.registerActivity();
    const dupKey = buildWordDuplicateKey(word);

    if (!isSupabaseConfigured || !supabase) {
      const local = this.getLocalWords();
      const isDup = local.some(w => buildWordDuplicateKey(w) === dupKey);
      if (isDup) {
        throw new Error("Esta palavra/frase já está cadastrada no Banco de Palavras.");
      }
      return this.createLocalWord(word);
    }

    try {
      const { data: userData, error: userError } = await supabase.auth.getUser();
      if (userError || !userData?.user) {
        throw new Error("Usuário não autenticado. Não é possível salvar palavra.");
      }

      console.log("[WordBankService] creating word", word);

      if (!word.term || !word.term.trim()) {
        throw new Error("A palavra ou termo não pode estar vazia.");
      }

      // Auto-translate if it's a generic auto-save string
      if (word.meaning && word.meaning.startsWith("Salvo via")) {
        try {
          const currentLang = i18n.language || "pt-BR";
          const targetLang = currentLang.split("-")[0];
          // don't translate if UI is French
          if (!currentLang.startsWith("fr")) {
            const res = await fetch(`https://api.mymemory.translated.net/get?q=${encodeURIComponent(word.term)}&langpair=fr|${targetLang}`);
            const data = await res.json();
            if (data && data.responseData && data.responseData.translatedText) {
              word.meaning = data.responseData.translatedText;
            }
          }
        } catch (err) {
          console.error("Translation API error during save:", err);
        }
      }

      if (!word.meaning || !word.meaning.trim()) {
        throw new Error("O significado ou tradução não pode estar vazio.");
      }

      // Direct lookup check
      const { data: existing, error: findError } = await supabase
        .from("word_bank")
        .select("id")
        .eq("duplicate_key", dupKey)
        .eq("user_id", userData.user.id)
        .maybeSingle();

      if (existing) {
        throw new Error("Esta palavra/frase já está cadastrada no Banco de Palavras.");
      }

      const row = {
        ...mapWordToSupabase(word),
        user_id: userData.user.id
      };
      const { data, error } = await supabase
        .from("word_bank")
        .insert([row])
        .select()
        .single();

      if (error) throw error;
      console.log("[WordBankService] created word", data);
      this.emitUpdates();
      return mapSupabaseToWord(data);
    } catch (err: any) {
      if (err.message === "Esta palavra/frase já está cadastrada no Banco de Palavras." || err.message === "A palavra ou termo não pode estar vazia." || err.message === "O significado ou tradução não pode estar vazio." || err.message?.includes("Usuário não autenticado")) {
        throw err;
      }
      throw handleSupabaseError(err, "word");
    }
  },

  // Update word
  async updateWord(id: string, word: Partial<WordBankRecord>): Promise<WordBankRecord> {
    const { data: userData, error: userError } = await supabase.auth.getUser();
    if (userError || !userData?.user) {
      throw new Error("Usuário não autenticado. Não é possível salvar palavra.");
    }

    const current = await this.getWordById(id);
    if (!current) throw new Error("Word not found");

    const merged = { ...current, ...word };
    const dupKey = buildWordDuplicateKey(merged);

    if (!isSupabaseConfigured || !supabase || id.startsWith("vocab_") || !getValidUuidOrNull(id)) {
      const local = this.getLocalWords();
      const isDup = local.some(w => w.id !== id && buildWordDuplicateKey(w) === dupKey);
      if (isDup) {
        throw new Error("Esta palavra/frase já está cadastrada no Banco de Palavras.");
      }
      return this.updateLocalWord(id, word);
    }

    try {
      if (!merged.term || !merged.term.trim()) {
        throw new Error("A palavra ou termo não pode estar vazia.");
      }
      if (!merged.meaning || !merged.meaning.trim()) {
        throw new Error("O significado ou tradução não pode estar vazio.");
      }

      // Direct lookup check
      const { data: existing, error: findError } = await supabase
        .from("word_bank")
        .select("id")
        .eq("duplicate_key", dupKey)
        .eq("user_id", userData.user.id)
        .neq("id", id)
        .maybeSingle();

      if (existing) {
        throw new Error("Esta palavra/frase já está cadastrada no Banco de Palavras.");
      }

      const row = {
        ...mapWordToSupabase(merged),
        user_id: userData.user.id
      };
      const { data, error } = await supabase
        .from("word_bank")
        .update(row)
        .eq("id", id)
        .eq("user_id", userData.user.id)
        .select()
        .single();

      if (error) throw error;
      this.emitUpdates();
      return mapSupabaseToWord(data);
    } catch (err: any) {
      if (err.message === "Esta palavra/frase já está cadastrada no Banco de Palavras." || err.message === "A palavra ou termo não pode estar vazia." || err.message === "O significado ou tradução não pode estar vazio.") {
        throw err;
      }
      throw handleSupabaseError(err, "word");
    }
  },

  // Delete word
  async deleteWord(id: string): Promise<void> {
    if (!isSupabaseConfigured || !supabase || id.startsWith("vocab_") || !getValidUuidOrNull(id)) {
      return this.deleteLocalWord(id);
    }
    try {
      const { data: userData, error: userError } = await supabase.auth.getUser();
      if (userError || !userData?.user) {
        throw new Error("Usuário não autenticado. Não é possível salvar palavra.");
      }

      const { error } = await supabase
        .from("word_bank")
        .delete()
        .eq("id", id)
        .eq("user_id", userData.user.id);

      if (error) throw error;
      this.emitUpdates();
    } catch (err: any) {
      throw handleSupabaseError(err, "word");
    }
  },

  // Delete multiple words
  async deleteMultipleWords(ids: string[]): Promise<number> {
    if (!ids || ids.length === 0) return 0;
    
    // Split local vs database IDs
    const dbIds = ids.filter(id => !id.startsWith("vocab_") && getValidUuidOrNull(id));
    const localIds = ids.filter(id => id.startsWith("vocab_") || !getValidUuidOrNull(id));

    // Handle local deletes
    if (localIds.length > 0) {
      const list = this.getLocalWords();
      const filtered = list.filter(w => !localIds.includes(w.id));
      localStorage.setItem(STORAGE_KEY, JSON.stringify(filtered));
    }

    // Handle database deletes
    if (isSupabaseConfigured && supabase && dbIds.length > 0) {
      try {
        const { data: userData, error: userError } = await supabase.auth.getUser();
        if (userError || !userData?.user) {
          throw new Error("Usuário não autenticado. Não é possível excluir palavras.");
        }

        const { error } = await supabase
          .from("word_bank")
          .delete()
          .in("id", dbIds)
          .eq("user_id", userData.user.id);

        if (error) throw error;
      } catch (err: any) {
        throw handleSupabaseError(err, "word");
      }
    }
    
    this.emitUpdates();
    return ids.length;
  },

  // Create flashcard from a word
  async createFlashcardFromWord(wordId: string): Promise<any> {
    streakService.registerActivity();
    const word = await this.getWordById(wordId);
    if (!word) throw new Error("Word not found");
    
    // Check active language
    const currentLang = i18n.language || "pt-BR";
    
    let front = "";
    let back = "";
    
    if (currentLang.startsWith("fr")) {
      front = word.term;
      back = word.term;
    } else {
      // For Portuguese (pt-BR), English (en), etc.
      if (!word.meaning || word.meaning.startsWith("Salvo via") || word.meaning === "Sem tradução") {
        try {
          const targetLang = currentLang.split("-")[0];
          const res = await fetch(`https://api.mymemory.translated.net/get?q=${encodeURIComponent(word.term)}&langpair=fr|${targetLang}`);
          const data = await res.json();
          if (data && data.responseData && data.responseData.translatedText) {
            front = data.responseData.translatedText;
          } else {
            front = word.meaning || "Sem tradução";
          }
        } catch (err) {
          console.error("Translation API error", err);
          front = word.meaning || "Sem tradução";
        }
      } else {
        front = word.meaning;
      }

      back = word.term;
      if (word.context) {
        back += `\n\nExemplo: ${word.context}`;
      }
    }
    
    const category = word.category || "Vocabulário";

    const cardPayload = {
      front,
      back,
      type: "word",
      language: "fr",
      category,
      source: "word_bank",
      sourceWordId: wordId,
      metadata: {
        word: word.term,
        translation: word.meaning,
        definition: word.meaning,
        exampleSentence: word.context,
        exampleTranslation: "",
        category: word.category
      }
    };

    return flashcardService.createFlashcard(cardPayload as any);
  },

  // --- LOCAL FALLBACK METHODS ---
  getLocalWords(): WordBankRecord[] {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (!saved) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(vocabularyData));
      return vocabularyData as WordBankRecord[];
    }
    try {
      return JSON.parse(saved);
    } catch {
      return vocabularyData as WordBankRecord[];
    }
  },

  createLocalWord(word: Omit<WordBankRecord, "id"> & { id?: string }): WordBankRecord {
    const list = this.getLocalWords();
    const newItem: WordBankRecord = {
      ...word,
      id: word.id || "vocab_" + Date.now() + "_" + Math.random().toString(36).slice(2, 5)
    };
    list.unshift(newItem);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
    this.emitUpdates();
    return newItem;
  },

  updateLocalWord(id: string, partial: Partial<WordBankRecord>): WordBankRecord {
    const list = this.getLocalWords();
    const idx = list.findIndex(w => w.id === id);
    if (idx === -1) {
      throw new Error(`Word ID ${id} not found locally.`);
    }
    const updated = { ...list[idx], ...partial };
    list[idx] = updated;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
    this.emitUpdates();
    return updated;
  },

  deleteLocalWord(id: string): void {
    const list = this.getLocalWords();
    const filtered = list.filter(w => w.id !== id);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filtered));
    this.emitUpdates();
  },

  emitUpdates() {
    window.dispatchEvent(new Event("vocabularyUpdated"));
    window.dispatchEvent(new Event("dashboardUpdated"));
  }
};
